import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import StatCard from 'components/DetailWidgets/StatCard'
import MultiLineChartCard from 'components/DetailWidgets/MultiLineChartCard'
import DonutCard from 'components/DetailWidgets/DonutCard'
import SimpleDataTable from 'components/DetailWidgets/SimpleDataTable'

const STAT_ICONS = {
  reached_users: UIcons.UilUsersAlt,
  unique_viewers: UIcons.UilEye,
  repeat_viewers: UIcons.UilSync,
  average_frequency: UIcons.UilChartLine,
  completed_viewers: UIcons.UilCheckCircle,
  reward_claims: UIcons.UilGift
}

const TARGETING_ICONS = {
  interests: UIcons.UilUsersAlt,
  locations: UIcons.UilLocationPinAlt,
  ipTargeting: UIcons.UilShieldCheck,
  devices: UIcons.UilDesktop,
  estimatedReach: UIcons.UilChartLine
}

const QUALITY_ICONS = {
  video_length: UIcons.UilPlayCircle,
  claim_rate: UIcons.UilGift,
  completion_rate: UIcons.UilCheckCircle,
  repeat_view_share: UIcons.UilSync
}

export function TargetingSummaryCard({ title, summary, t }) {
  const items = [
    { key: 'interests', label: t('rewardAds.detail.targeting_schedule.interests'), value: summary.interests },
    { key: 'locations', label: t('rewardAds.detail.targeting_schedule.location'), value: summary.locations },
    { key: 'ipTargeting', label: t('rewardAds.detail.targeting_schedule.ip_targeting'), value: summary.ipTargeting },
    { key: 'devices', label: t('rewardAds.detail.targeting_schedule.devices'), value: summary.devices },
    { key: 'estimatedReach', label: t('rewardAds.detail.targeting_schedule.estimated_reach'), value: summary.estimatedReach }
  ]

  return (
    <div className="w-full h-full rounded-2xl border border-border-lines p-4">
      <p className="text-base font-semibold text-dark-blue mb-4">{title}</p>
      <div className="flex flex-col gap-3.5">
        {
          items.map((item) => {
            const Icon = TARGETING_ICONS[item.key]
            return (
              <div key={item.key} className="flex items-start gap-2.5">
                <Icon size={16} className="text-secondary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs text-secondary">{item.label}</p>
                  <p className="text-sm text-dark-blue">{item.value}</p>
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  )
}

export function AudienceQualityCard({ title, items }) {
  return (
    <div className="w-full h-full rounded-2xl border border-border-lines p-4">
      <p className="text-base font-semibold text-dark-blue mb-4">{title}</p>
      <div className="grid grid-cols-2 gap-4">
        {
          items.map((item) => {
            const Icon = QUALITY_ICONS[item.key] || UIcons.UilChartLine
            return (
              <div key={item.key} className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-secondary truncate">{item.label}</p>
                  <p className="text-base font-semibold text-dark-blue">{item.value}</p>
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  )
}

export default withTranslation()(function AudienceTab({ t, campaign }) {
  const { audience } = campaign

  const cohortColumns = [
    { key: 'cohort', label: t('rewardAds.detail.audience.table.cohort') },
    { key: 'uniqueViewers', label: t('rewardAds.detail.audience.table.unique_viewers') },
    { key: 'completionRate', label: t('rewardAds.detail.audience.table.completion_rate') },
    { key: 'ctaClickRate', label: t('rewardAds.detail.audience.table.cta_click_rate') },
    { key: 'rewardClaims', label: t('rewardAds.detail.audience.table.reward_claims') }
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {
          audience.stats.map((stat) => (
            <StatCard
              key={stat.key}
              icon={STAT_ICONS[stat.key]}
              label={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              trend={stat.trend}
              tone={stat.tone}
            />
          ))
        }
      </div>

      <MultiLineChartCard title={t('rewardAds.detail.audience.reach_over_time')} series={audience.reachOverTime.series} />

      <div className="grid md:grid-cols-2 gap-4">
        <DonutCard title={t('rewardAds.detail.audience.viewer_frequency_distribution')} data={audience.viewerFrequencyDistribution} />
        <TargetingSummaryCard t={t} title={t('rewardAds.detail.audience.targeting_summary')} summary={audience.targetingSummary} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <DonutCard title={t('rewardAds.detail.audience.viewer_type_breakdown')} data={audience.viewerTypeBreakdown} />
        <AudienceQualityCard title={t('rewardAds.detail.audience.audience_quality')} items={audience.audienceQuality} />
      </div>

      <SimpleDataTable
        title={t('rewardAds.detail.audience.engagement_by_cohort')}
        columns={cohortColumns}
        rows={audience.engagementByCohort}
      />
    </div>
  )
})
